import { ref, onUnmounted } from 'vue';
import { useSpotifyAuth } from './useSpotifyAuth';
import type { SpotifyPlayerState } from '@/types/spotify';

// Globaler State
const player = ref<Spotify.Player | null>(null);
const deviceId = ref<string | null>(null);
const isReady = ref(false);
const isPlaying = ref(false);
const currentTrack = ref<SpotifyPlayerState['track_window']['current_track'] | null>(null);

// interne Promises, damit wir nicht mehrfach initialisieren
let sdkScriptLoading: Promise<void> | null = null;
let playerInitPromise: Promise<boolean> | null = null;

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady: () => void;
    Spotify: {
      Player: new (options: Spotify.PlayerOptions) => Spotify.Player;
    };
  }
}

export function useSpotifyPlayer() {
  const { getAccessToken } = useSpotifyAuth();

  /**
   * Lädt NUR das SDK-Script (ohne Player-Init)
   */
  function loadSpotifySDKScript(): Promise<void> {
    if (sdkScriptLoading) return sdkScriptLoading;

    sdkScriptLoading = new Promise<void>((resolve, reject) => {
      // Script schon im DOM?
      const existing = document.querySelector<HTMLScriptElement>(
        'script[src="https://sdk.scdn.co/spotify-player.js"]'
      );
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      script.onload = () => {
        console.log('[SpotifyPlayer] SDK Script geladen');
        resolve();
      };
      script.onerror = () => reject(new Error('Fehler beim Laden des Spotify SDK'));
      document.body.appendChild(script);
    });

    return sdkScriptLoading;
  }

  /**
   * Initialisiert den Player.
   * Wichtig: Player wird direkt im onSpotifyWebPlaybackSDKReady-Callback erzeugt.
   */
  function initPlayer(): Promise<boolean> {
    if (playerInitPromise) return playerInitPromise;

    playerInitPromise = new Promise<boolean>((resolve, reject) => {
      const token = getAccessToken();
      if (!token) {
        console.error('[SpotifyPlayer.initPlayer] Kein Access Token vorhanden');
        reject(new Error('Kein Access Token'));
        return;
      }

      const startInit = () => {
        try {
          // Falls schon vorhanden (mehrfacher Aufruf)
          if (player.value) {
            console.log('[SpotifyPlayer.initPlayer] Player existiert bereits');
            resolve(true);
            return;
          }

          console.log('[SpotifyPlayer.initPlayer] Erstelle neuen Spotify.Player...');

          const p = new window.Spotify.Player({
            name: 'SoundBattle Web Player',
            getOAuthToken: (cb: (token: string) => void) => {
              cb(token);
            },
            volume: 0.8
          });

          player.value = p;

          // Events
          p.addListener('ready', ({ device_id }: { device_id: string }) => {
            console.log('[SpotifyPlayer] Player bereit mit Device ID:', device_id);
            deviceId.value = device_id;
            isReady.value = true;
          });

          p.addListener('not_ready', ({ device_id }: { device_id: string }) => {
            console.log('[SpotifyPlayer] Device ID offline:', device_id);
            isReady.value = false;
          });

          p.addListener('player_state_changed', (state: SpotifyPlayerState | null) => {
            if (!state) return;
            isPlaying.value = !state.paused;
            currentTrack.value = state.track_window.current_track;
            console.log('[SpotifyPlayer] player_state_changed, paused =', state.paused);
          });

          p.addListener('initialization_error', ({ message }: { message: string }) => {
            console.error('[SpotifyPlayer] Initialization Error:', message);
          });

          p.addListener('authentication_error', ({ message }: { message: string }) => {
            console.error('[SpotifyPlayer] Authentication Error:', message);
          });

          p.addListener('account_error', ({ message }: { message: string }) => {
            console.error('[SpotifyPlayer] Account Error:', message);
          });

          p.addListener('playback_error', ({ message }: { message: string }) => {
            console.error('[SpotifyPlayer] Playback Error:', message);
          });

          // Hier direkt connecten
          p.connect().then(success => {
            console.log('[SpotifyPlayer.initPlayer] connect() returned:', success);
            if (!success) {
              reject(new Error('Spotify Player konnte nicht verbunden werden'));
            } else {
              resolve(true);
            }
          }).catch(err => {
            reject(err);
          });
        } catch (err) {
          reject(err as Error);
        }
      };

      // Fall 1: Spotify SDK ist schon da
      if ((window as any).Spotify) {
        console.log('[SpotifyPlayer.initPlayer] window.Spotify bereits verfügbar → init direkt');
        startInit();
        return;
      }

      // Fall 2: Spotify SDK noch nicht da → Callback setzen, DANN Script laden
      console.log('[SpotifyPlayer.initPlayer] Registriere onSpotifyWebPlaybackSDKReady Callback');
      window.onSpotifyWebPlaybackSDKReady = () => {
        console.log('[SpotifyPlayer] onSpotifyWebPlaybackSDKReady fired');
        startInit();
      };

      // Script laden, Fehler ggf. nach außen reichen
      loadSpotifySDKScript().catch(err => {
        reject(err);
      });
    });

    return playerInitPromise;
  }

  /**
   * Public connect(): stellt sicher, dass der Player initialisiert ist
   */
  async function connect(): Promise<boolean> {
    try {
      const ok = await initPlayer();
      return ok;
    } catch (err) {
      console.error('[SpotifyPlayer.connect] Fehler bei initPlayer():', err);
      return false;
    }
  }

  /**
   * Track abspielen
   */
  async function playTrack(trackUri: string): Promise<void> {
    const token = getAccessToken();
    if (!token || !deviceId.value) {
      console.error('[SpotifyPlayer.playTrack] Player nicht bereit', {
        hasToken: !!token,
        deviceId: deviceId.value
      });
      throw new Error('Player nicht bereit');
    }

    console.log('[SpotifyPlayer.playTrack] Starte Wiedergabe', {
      trackUri,
      deviceId: deviceId.value
    });

    const response = await fetch(
      `https://api.spotify.com/v1/me/player/play?device_id=${deviceId.value}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          uris: [trackUri]
        })
      }
    );

    console.log('[SpotifyPlayer.playTrack] response.status =', response.status);

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.error('[SpotifyPlayer.playTrack] Fehler-Response:', text);
      throw new Error(`Playback-Fehler: ${response.status}`);
    }
  }

  async function pause(): Promise<void> {
    if (!player.value) return;
    await player.value.pause();
  }

  async function resume(): Promise<void> {
    if (!player.value) return;
    await player.value.resume();
  }

  function disconnect() {
    if (player.value) {
      player.value.disconnect();
      player.value = null;
      deviceId.value = null;
      isReady.value = false;
    }
  }

  onUnmounted(() => {
    // Player global behalten
  });

  return {
    player,
    deviceId,
    isReady,
    isPlaying,
    currentTrack,
    connect,
    playTrack,
    pause,
    resume,
    disconnect
  };
}
